import { AdminGroup } from '../../../api/group/groupAdminApiSlice';
import { UseCase } from '../../../tenants/config';

/**
 * The groups a code may be mapped to: those whose compartment/use-case types
 * the tenant actually offers.
 *
 * Second line of defence against offering a group the tenant does not own.
 * Tenant isolation itself lives in the backend (`X-Tenant` → per-tenant DB /
 * `tenant_id` scoping) plus the central cache reset on tenant change
 * (`store/tenantCacheResetMiddleware`); this additionally drops anything whose
 * use case the tenant does not offer, so e.g. a product-rental tenant can never
 * be offered an apartment or venue group.
 *
 * Note what it deliberately does NOT claim. It narrows the blast radius; it is
 * not the boundary:
 *   - a foreign group that shares a use case with the tenant still passes;
 *   - `services` comes from the same tenant config that the cache reset fixes,
 *     and it is written by the same App.tsx effect — so during the window where
 *     stale caches were visible, `services` was stale too and the old tenant's
 *     groups would have matched it. The reset is what closes that window.
 *
 * Fails CLOSED — an empty `services` list (tenant config not resolved yet)
 * yields no selectable group rather than falling back to everything. Config
 * resolves before the main layout mounts, so this costs nothing in practice,
 * and an empty select is a recoverable annoyance where offering a foreign group
 * is not.
 *
 * Compared case-insensitively per the API-values rule.
 */
export const selectableGroupsForServices = (
    groups: AdminGroup[] | undefined,
    services: UseCase[],
): AdminGroup[] => {
    const offered = new Set(services.map((service) => service.toLowerCase()));

    return (groups ?? []).filter((group) =>
        group.compartmentTypes.some((type) => offered.has(type.toLowerCase())),
    );
};
